<template>
  <v-dialog
    v-model="visible"
    :fullscreen="fullscreen"
    width="600"
    :persistent="dialog.config.persistent"
    @click:outside="closeIfClosable"
  >
    <component :is="currentComponent" />
    <slot />
  </v-dialog>
</template>

<script setup lang="ts">

/**
 * This component is responsible for displaying a dialog with dynamic content based on the current route.
 *
 * It has a custom router, exposed on the dialog Pinia store that allows navigation between different dialog views.
 *
 * The component itself doesn't render a <router-view>, instead it mantains the current route component on a local reference
 * and renders it using a dynamic component. This behavior was changed during the migration to Vue 3 and Vue Router 4,
 * as the previous approach with a dedicated <router-view> inside the component isn't supported (AFAIK).
 *
 * I've opted to keep the interface router-based to keep existing uses and honestly because it feels nice to use.
 */

import { computed, watch, onBeforeMount, shallowRef, markRaw } from "vue";
import { createRouter, createMemoryHistory } from "vue-router";
import type { RouteComponent } from "vue-router";
import { useDisplay } from "vuetify";
import { useDialog } from "@/stores/dialog";
import { routes } from "@/router/routes/dialog";
import NotFound from "./NotFound.vue";

// Router
const router = createRouter({
  history: createMemoryHistory(import.meta.env.BASE_URL),
  routes,
});

// Composables
const { mobile } = useDisplay();
const dialog = useDialog();

// Use shallowRef for component to avoid deep reactivity on component objects
const currentComponent = shallowRef<RouteComponent>(markRaw(NotFound));

// Computed
const visible = computed({
  get: () => dialog.visible,
  set: () => dialog.hide(),
});

const fullscreen = computed(() => {
  if (dialog.config.fullscreen !== undefined) {
    return dialog.config.fullscreen;
  }
  return mobile.value;
});

// Watchers
watch(
  () => dialog.route,
  async (newRoute) => {
    await router.push(newRoute);
    const matched = router.currentRoute.value.matched[0];
    if (matched?.components?.dialog) {
      currentComponent.value = markRaw(matched.components.dialog);
      return;
    };

    currentComponent.value = markRaw(NotFound);
  },
  // Renders dialogs opened before mount (e.g. by router middlewares on first navigation)
  { immediate: true },
);

// Lifecycle
onBeforeMount(() => {
  if (dialog.visible) return;

  dialog.hide();
  router.push("/");
});

// Methods
const closeIfClosable = () => {
  if (dialog.config.persistent) return;
  dialog.hide();
};
</script>
